import { ChangeEvent, Children, FC, ReactElement, cloneElement, useCallback } from 'react';
import { FormGroup, FormControlLabel, FormHelperText, FormLabel, makeStyles, Checkbox, Radio, RadioGroup, Box, Typography } from '@material-ui/core';
import { ErrorMessage, HelpTooltip } from '../helpers';

export default ({ children, hideLabel, onChange, value, helperText, label, name, info, disabled, multiple, options = [] }: {
    options?: string[] | Record<string, string>;
    error?: boolean;
    hideLabel?: boolean;
    onChange?: (event: ChangeEvent<HTMLInputElement>, value: string) => void;
    value?: string;
    helperText?: string;
    label?: string;
    name?: string;
    info?: string;
    disabled?: boolean;
    multiple?: boolean;
    children?: ReactElement<OptionProps> | ReactElement<OptionProps>[];
}) => {
    const classes = useStyles();

    const checkChanged = useCallback((event, option) => {
        const {target} = event;
        const {checked} = target;
        const values = (value || '').split(',').filter(v => v);
        let newValues = values;

        if (!checked) {
            newValues = values.filter(v => v !== option);
        } else if (!values.includes(option)) {
            newValues = [...values, option];
        }

        onChange && onChange({...event, target: {...target, value: newValues.join(',')}}, newValues.join(','));
    }, [value, onChange]);

    const formattedOptions = Array.isArray(options) ? options.map(o => [o, o]) : Object.entries(options);
    const optionElements = children ?? formattedOptions.map(([k, v]) => <Option value={k} label={v} key={k} />) ?? [];

    return <>
        {!hideLabel &&
            <Box style={{ display: 'flex' }}>
                <FormLabel style={{ lineHeight: 1.2 }}>{label || name}</FormLabel>
                <HelpTooltip title={info} />
            </Box>
        }
        <Group multiple={multiple} onChange={onChange} value={value} name={name}>
            {Children.map(optionElements, (child) =>
                cloneElement(child, {
                    selector: multiple ?
                        <Checkbox
                            checked={(value || '').split(',').includes(child?.props?.value)}
                            onChange={event => checkChanged(event, child?.props?.value)}
                            className={classes.selector}
                        />
                        : <Radio className={classes.selector} />,
                    ...disabled && { disabled }
                })
            )}
        </Group>
        {!!helperText ? <FormHelperText error>{helperText}</FormHelperText> : <ErrorMessage name={name} />}
    </>
}

interface GroupProps {
    multiple?: boolean
    name?: string
    value?: string
    onChange?: (event: ChangeEvent<HTMLInputElement>, value: string) => void
}

const Group: FC<GroupProps> = ({ children, multiple, name, onChange, value }) => multiple ? <FormGroup>{children}</FormGroup> : <RadioGroup name={name} value={value} onChange={onChange}>{children}</RadioGroup>

interface OptionProps {
    label: string;
    info?: string;
    value: any;
    disabled?: boolean;
    selector?: ReactElement;
}

export const Option: FC<OptionProps> = ({ label, value, disabled, selector, info, children }) => {
    const classes = useStyles();

    return <FormControlLabel
        labelPlacement="start"
        control={selector ?? <></>}
        value={value}
        disabled={disabled}
        classes={{ root: classes.option, label: classes.optionLabel }}
        label={<>
            <Typography style={{ display: 'flex', alignItems: 'center' }}>
                {label}
                <HelpTooltip title={info} />
            </Typography>
            {children}
        </>}
    />
}

const useStyles = makeStyles({
    option: {
        margin: '3px 0'
    },
    optionLabel: {
        margin: '0 0 6px 0',
        flexGrow: 1
    },
    selector: {
        alignSelf: 'start',
        marginTop: -9,
        marginRight: -9
    }
}, { name: 'ListSelect' });
